import { supabase } from './supabase';
import bcrypt from 'bcryptjs';

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  role: string;
  is_active: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: AdminUser;
  error?: string;
}

// Chiave di sessione nel localStorage
const SESSION_KEY = 'supabase_admin_session';

export const supabaseAuth = {
  /**
   * Login admin
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Cerca l'utente nel database
      const { data: users, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('username', credentials.username)
        .eq('is_active', true)
        .limit(1);

      if (error) {
        console.error('Supabase login error:', error);
        return { success: false, error: 'Errore durante il login' };
      }

      if (!users || users.length === 0) {
        return { success: false, error: 'Credenziali non valide' };
      }

      const user = users[0];

      // Verifica password (confronto diretto per semplicità)
      // In produzione, usa bcrypt.compare() se hai hashato le password
      const isPasswordValid = user.password_hash === credentials.password ||
        await bcrypt.compare(credentials.password, user.password_hash);

      if (!isPasswordValid) {
        return { success: false, error: 'Credenziali non valide' };
      }

      // Crea sessione
      const adminUser: AdminUser = {
        id: user.id,
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        is_active: user.is_active,
      };

      // Salva sessione in localStorage
      localStorage.setItem(SESSION_KEY, JSON.stringify(adminUser));

      // Log attività
      await this.logActivity(user.id, 'login', 'admin_user', user.id, {
        username: user.username,
      });

      return { success: true, user: adminUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Errore imprevisto durante il login' };
    }
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    const session = this.getSession();
    if (session) {
      await this.logActivity(session.id, 'logout', 'admin_user', session.id);
    }
    localStorage.removeItem(SESSION_KEY);
  },

  /**
   * Ottieni sessione corrente
   */
  getSession(): AdminUser | null {
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;

    try {
      return JSON.parse(sessionData);
    } catch {
      return null;
    }
  },

  /**
   * Verifica se l'utente è autenticato
   */
  isAuthenticated(): boolean {
    return this.getSession() !== null;
  },

  /**
   * Log attività admin
   * entityId è string per supportare ID legacy integer
   */
  async logActivity(
    adminId: string,
    action: string,
    entityType?: string,
    entityId?: string,
    details?: any
  ): Promise<void> {
    try {
      await supabase.from('activity_log').insert({
        admin_id: adminId,
        action,
        entity_type: entityType,
        entity_id: entityId, // TEXT supporta sia UUID che integer legacy
        details,
        ip_address: 'unknown', // Puoi ottenere l'IP da una API esterna
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  },
};
